const TRACE_FIELDS = Object.freeze([
  Object.freeze({ key: "pageArea", label: "Page area", attribute: "page.area", sample: "map" }),
  Object.freeze({ key: "deploymentEnvironment", label: "Deployment environment", attribute: "deployment.environment", sample: "beta" }),
  Object.freeze({ key: "serviceVersion", label: "FishyStuff release", attribute: "service.version", sample: "abc123" }),
  Object.freeze({ key: "operationName", label: "Bounded operation name", attribute: "operation.name", sample: "api.request" }),
  Object.freeze({ key: "durationMs", label: "Duration in milliseconds", attribute: "duration_ms", sample: 125 }),
  Object.freeze({ key: "status", label: "Success or error status", attribute: "status", sample: "ok" }),
]);

const RUM_FIELDS = Object.freeze([
  Object.freeze({ key: "pageArea", label: "Page area", attribute: "page.area", sample: "map" }),
  Object.freeze({ key: "deploymentEnvironment", label: "Deployment environment", attribute: "deployment.environment", sample: "beta" }),
  Object.freeze({ key: "serviceVersion", label: "FishyStuff release", attribute: "service.version", sample: "abc123" }),
  Object.freeze({ key: "eventType", label: "Event category", attribute: "event.type", sample: "web_vital" }),
  Object.freeze({ key: "metricName", label: "Metric name", attribute: "metric.name", sample: "LCP" }),
  Object.freeze({ key: "metricValue", label: "Numeric metric value", attribute: "metric.value", sample: 1234.5 }),
  Object.freeze({ key: "metricRating", label: "Metric rating", attribute: "metric.rating", sample: "good" }),
  Object.freeze({ key: "errorClass", label: "Allowlisted JavaScript error class", attribute: "error.class", sample: "TypeError" }),
]);

function exactRecord(value, fields) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  return actual.length === expected.length
    && actual.every((field, index) => field === expected[index]);
}

function selectedFields(definitions) {
  return Object.freeze(Object.fromEntries(definitions.map(({ key }) => [key, true])));
}

export function disabledTelemetrySelection() {
  return Object.freeze({
    traces: Object.freeze({ enabled: false, fields: selectedFields(TRACE_FIELDS) }),
    rum: Object.freeze({ enabled: false, fields: selectedFields(RUM_FIELDS) }),
  });
}

function decodeSignal(value, definitions, label) {
  const fieldNames = definitions.map(({ key }) => key);
  if (
    !exactRecord(value, ["enabled", "fields"])
    || typeof value.enabled !== "boolean"
    || !exactRecord(value.fields, fieldNames)
    || fieldNames.some((field) => typeof value.fields[field] !== "boolean")
  ) {
    throw new TypeError(`${label} telemetry selection is invalid.`);
  }
  return Object.freeze({ enabled: value.enabled, fields: Object.freeze({ ...value.fields }) });
}

export function decodeTelemetrySelection(value) {
  if (!exactRecord(value, ["traces", "rum"])) {
    throw new TypeError("Telemetry selection is invalid.");
  }
  return Object.freeze({
    traces: decodeSignal(value.traces, TRACE_FIELDS, "Trace"),
    rum: decodeSignal(value.rum, RUM_FIELDS, "RUM"),
  });
}

export function telemetrySelectionActive(selection) {
  const decoded = decodeTelemetrySelection(selection);
  return decoded.traces.enabled || decoded.rum.enabled;
}

function selectedAttributes(signal, definitions) {
  return Object.freeze(Object.fromEntries(
    definitions
      .filter(({ key }) => signal.fields[key])
      .map(({ attribute, sample }) => [attribute, sample]),
  ));
}

export function telemetrySelectionPreview(selection, { serviceName = "fishystuff-site" } = {}) {
  const decoded = decodeTelemetrySelection(selection);
  return Object.freeze({
    schemaVersion: 1,
    traces: decoded.traces.enabled
      ? Object.freeze({
        fixedEnvelope: Object.freeze({
          traceId: "<generated 32-hex trace ID>",
          spanId: "<generated 16-hex span ID>",
          startedAt: "<event timestamp>",
          spanName: "api.request",
          instrumentationScope: "fishystuff.browser",
          endedAt: "<event timestamp>",
          serviceName,
        }),
        selectedAttributes: selectedAttributes(decoded.traces, TRACE_FIELDS),
      })
      : null,
    rum: decoded.rum.enabled
      ? Object.freeze({
        fixedEnvelope: Object.freeze({
          observedAt: "<event timestamp>",
          serviceName,
          signal: "rum",
          severity: "INFO",
          instrumentationScope: "fishystuff.browser.rum",
        }),
        selectedAttributes: selectedAttributes(decoded.rum, RUM_FIELDS),
      })
      : null,
  });
}

export function telemetrySelectionWith(selection, signal, patch) {
  const decoded = decodeTelemetrySelection(selection);
  if (!Object.hasOwn(decoded, signal)) throw new TypeError("Unknown telemetry signal.");
  return decodeTelemetrySelection({
    ...decoded,
    [signal]: {
      ...decoded[signal],
      ...patch,
      fields: { ...decoded[signal].fields, ...(patch.fields || {}) },
    },
  });
}

export const TRACE_TELEMETRY_FIELDS = TRACE_FIELDS;
export const RUM_TELEMETRY_FIELDS = RUM_FIELDS;
